import { queryOptions } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const newsByCategoryQuery = (category?: string, limit = 12) =>
  queryOptions({
    queryKey: ["news", category ?? "all", limit],
    queryFn: async () => {
      let q = supabase
        .from("news_articles")
        .select("id,slug,title,excerpt,category,author,published_at,cover_image")
        .order("published_at", { ascending: false })
        .limit(limit);
      if (category) q = q.eq("category", category as never);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

export const articleBySlugQuery = (slug: string) =>
  queryOptions({
    queryKey: ["news-article", slug],
    queryFn: async () => {
      const select = "id,slug,title,excerpt,category,author,published_at,cover_image,body";
      const { data, error } = await supabase
        .from("news_articles")
        .select(select)
        .eq("slug", slug)
        .maybeSingle();
      if (error) throw error;
      if (data) return data;

      const uuidLike = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(slug);
      if (!uuidLike) return null;
      const { data: byId, error: idError } = await supabase
        .from("news_articles")
        .select(select)
        .eq("id", slug)
        .maybeSingle();
      if (idError) throw idError;
      return byId;
    },
  });

export const fixturesQuery = (scope: string, limit = 20) =>
  queryOptions({
    queryKey: ["fixtures", scope, limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fixtures")
        .select("*")
        .eq("team_scope", scope as never)
        .order("kickoff", { ascending: true })
        .limit(limit);
      if (error) throw error;
      return data ?? [];
    },
  });

export const squadQuery = (scope: string) =>
  queryOptions({
    queryKey: ["squad", scope],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("squad_members")
        .select("*")
        .eq("team_scope", scope as never)
        .order("shirt_number", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

export const leagueTableQuery = () =>
  queryOptions({
    queryKey: ["league-table"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("league_table")
        .select("*")
        .order("position", { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
  });

export const pollsQuery = () =>
  queryOptions({
    queryKey: ["polls"],
    queryFn: async () => {
      const { data: polls, error } = await supabase
        .from("polls")
        .select("id,question,is_active")
        .eq("is_active", true);
      if (error) throw error;
      const { data: options } = await supabase.from("poll_options").select("*").order("sort_order");
      const { data: counts } = await supabase.rpc("get_poll_vote_counts");
      return (polls ?? []).map((p) => {
        const opts = (options ?? []).filter((o) => o.poll_id === p.id);
        const pollCounts = (counts ?? []).filter((c: { poll_id: string }) => c.poll_id === p.id);
        const total = pollCounts.reduce((sum: number, c: { votes: number }) => sum + Number(c.votes), 0);
        return {
          ...p,
          options: opts.map((o) => ({
            ...o,
            votes: Number(pollCounts.find((c: { option_id: string }) => c.option_id === o.id)?.votes ?? 0),
          })),
          total,
        };
      });
    },
  });

export const productsQuery = (category?: string) =>
  queryOptions({
    queryKey: ["products", category ?? "all"],
    queryFn: async () => {
      let q = supabase.from("shop_products").select("*").order("sold_count", { ascending: false });
      if (category) q = q.eq("category", category);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });

export const forumThreadsQuery = () =>
  queryOptions({
    queryKey: ["forum-threads"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("forum_threads")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

export const jobsQuery = () =>
  queryOptions({
    queryKey: ["jobs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("job_listings")
        .select("*")
        .eq("is_open", true)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });
